import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { 
    Clock, 
    Check, 
    X, 
    AlertCircle, 
    Calendar,
    User,
    RefreshCw,
    Timer,
    TrendingUp,
    History
} from 'lucide-react';

const OvertimePage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [pendingOvertime, setPendingOvertime] = useState([]);
    const [historyData, setHistoryData] = useState([]);
    const [stats, setStats] = useState({ total_pending: 0, total_overtime_hours: 0 });
    const [activeTab, setActiveTab] = useState('pending');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [pendingRes, historyRes] = await Promise.all([
                api().get('/overtime/pending'),
                api().get('/overtime/history')
            ]);
            setPendingOvertime(pendingRes.data.pending || []);
            setStats({
                total_pending: pendingRes.data.total_pending,
                total_overtime_hours: pendingRes.data.total_overtime_hours
            });
            setHistoryData(historyRes.data.history || []);
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error('Accès réservé à l\'administrateur');
            } else {
                toast.error('Erreur lors du chargement');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleValidate = async (attendanceId) => {
        try {
            await api().put(`/overtime/${attendanceId}/validate`);
            toast.success('Heures supplémentaires validées');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
        setShowConfirmDialog(false);
    };

    const handleReject = async (attendanceId) => {
        try {
            await api().put(`/overtime/${attendanceId}/reject`);
            toast.success('Heures supplémentaires rejetées');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
        setShowConfirmDialog(false);
    };

    const openConfirmDialog = (record, action) => {
        setSelectedRecord(record);
        setConfirmAction(action);
        setShowConfirmDialog(true);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
        });
    };

    const formatTime = (isoStr) => {
        if (!isoStr) return '-';
        return new Date(isoStr).toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    if (user?.role !== 'ADMIN') {
        return (
            <div className="p-6">
                <Card className="max-w-md mx-auto">
                    <CardContent className="pt-6 text-center">
                        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                        <h2 className="text-lg font-semibold mb-2">Accès Restreint</h2>
                        <p className="text-muted-foreground">
                            Cette page est réservée aux administrateurs.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Clock className="w-7 h-7 text-primary" />
                        Heures Supplémentaires
                    </h1>
                    <p className="text-muted-foreground">
                        Validation des heures sup (après 10h/jour, +25%)
                    </p>
                </div>
                <Button variant="outline" onClick={fetchData} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-100">
                                <AlertCircle className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.total_pending}</p>
                                <p className="text-xs text-muted-foreground">En attente</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                                <Timer className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.total_overtime_hours.toFixed(1)}h</p>
                                <p className="text-xs text-muted-foreground">Heures en attente</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100">
                                <Check className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{historyData.length}</p>
                                <p className="text-xs text-muted-foreground">Validées (total)</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-100">
                                <TrendingUp className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">+25%</p>
                                <p className="text-xs text-muted-foreground">Majoration</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b">
                <button
                    onClick={() => setActiveTab('pending')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'pending' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    En attente ({pendingOvertime.length})
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'history' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    <History className="w-4 h-4 inline mr-2" />
                    Historique
                </button>
            </div>

            {/* Content */}
            {activeTab === 'pending' && (
                <div className="space-y-3">
                    {loading ? (
                        <Card>
                            <CardContent className="py-8 text-center">
                                <RefreshCw className="w-8 h-8 mx-auto animate-spin text-muted-foreground" />
                                <p className="mt-2 text-muted-foreground">Chargement...</p>
                            </CardContent>
                        </Card>
                    ) : pendingOvertime.length === 0 ? (
                        <Card>
                            <CardContent className="py-8 text-center">
                                <Check className="w-12 h-12 mx-auto text-green-500 mb-3" />
                                <h3 className="font-medium">Aucune heure sup en attente</h3>
                                <p className="text-sm text-muted-foreground">
                                    Toutes les heures supplémentaires ont été traitées
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        pendingOvertime.map(record => (
                            <Card key={record.id} className="overflow-hidden">
                                <CardContent className="p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                                <User className="w-6 h-6 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold">{record.staff_name}</h3>
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(record.date)}
                                                    <span className="text-xs">|</span>
                                                    <span>{record.staff_service}</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                            <div className="text-center px-4 py-2 bg-muted/50 rounded-lg">
                                                <div className="text-xs text-muted-foreground">Travaillé</div>
                                                <div className="font-mono font-bold">{record.total_work_hours}h</div>
                                            </div>
                                            <div className="text-center px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                                <div className="text-xs text-amber-600">Heures sup</div>
                                                <div className="font-mono font-bold text-amber-700">+{record.overtime_hours}h</div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button 
                                                    size="sm" 
                                                    variant="outline"
                                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                                    onClick={() => openConfirmDialog(record, 'reject')}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    size="sm"
                                                    className="bg-green-600 hover:bg-green-700"
                                                    onClick={() => openConfirmDialog(record, 'validate')}
                                                >
                                                    <Check className="w-4 h-4 mr-1" />
                                                    Valider
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-muted-foreground">
                                        <span>Arrivée: {formatTime(record.check_in_time)}</span>
                                        <span>Départ: {formatTime(record.check_out_time)}</span>
                                        <span>Prévu: {record.scheduled_start} - {record.scheduled_end}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'history' && (
                <Card>
                    <CardContent className="p-0">
                        {historyData.length === 0 ? (
                            <div className="py-8 text-center">
                                <History className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                                <h3 className="font-medium">Aucun historique</h3>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {historyData.map(record => (
                                    <div key={record.id} className="p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                                <Check className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div>
                                                <div className="font-medium">{record.staff_name}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {formatDate(record.date)}
                                                </div>
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                            +{record.overtime_hours}h
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Confirm Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {confirmAction === 'validate' ? (
                                <>
                                    <Check className="w-5 h-5 text-green-600" />
                                    Valider les heures sup
                                </>
                            ) : (
                                <>
                                    <X className="w-5 h-5 text-red-600" />
                                    Rejeter les heures sup
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedRecord && (
                        <div className="py-4">
                            <div className="text-center p-4 bg-muted/50 rounded-lg mb-4">
                                <div className="font-semibold">{selectedRecord.staff_name}</div>
                                <div className="text-sm text-muted-foreground">{formatDate(selectedRecord.date)}</div>
                                <div className="text-2xl font-bold text-primary mt-2">
                                    +{selectedRecord.overtime_hours}h
                                </div>
                            </div>
                            
                            <p className="text-sm text-muted-foreground text-center">
                                {confirmAction === 'validate' 
                                    ? 'Ces heures seront comptabilisées avec une majoration de +25%.'
                                    : 'Ces heures supplémentaires seront annulées.'}
                            </p>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={() => confirmAction === 'validate' 
                                ? handleValidate(selectedRecord.id) 
                                : handleReject(selectedRecord.id)
                            }
                            className={confirmAction === 'validate' 
                                ? 'bg-green-600 hover:bg-green-700' 
                                : 'bg-red-600 hover:bg-red-700'
                            }
                        >
                            {confirmAction === 'validate' ? 'Confirmer la validation' : 'Confirmer le rejet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default OvertimePage;
